'use client';

import { FC, useEffect, useContext, useState, useMemo } from 'react';
import { ClientProfileEntityEditorProps } from './ClientProfileEntityEditor.types';
import {
  Flex,
  Card,
  SplitView,
  Input,
  Button,
  ButtonGroup,
  Select,
} from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { Company } from '@/_types/models/company';
import { updateCompany } from '@/_api/client-companies';
import { useParams } from 'next/navigation';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { User } from '@/_types/models/user';
import { COUNTRY_ITEMS } from '@/_constants/countries';
import { countries } from 'countries-list';

export const ClientProfileEntityEditor: FC<ClientProfileEntityEditorProps> = ({
  entityId,
  isPreview,
}) => {
  const { previewUserId } = useParams();
  const { isAdvisor, user, token, companies, setCompanies } =
    useContext(UserContext);
  const { previewUser, previewUserCompanies } = useContext(UserPreviewContext);

  const clientProfileUser: User | undefined = useMemo(
    () => (!!previewUserId ? previewUser : user),
    [previewUserId, previewUser, user],
  );

  const { addToast } = useContext(ToastContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [businessEmail, setBusinessEmail] = useState<string>('');
  const [otherEmail, setOtherEmail] = useState<string>('');
  const [streetAddress, setStreetAddress] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [zip, setZip] = useState<string>('');
  const [country, setCountry] = useState<string>('');

  const isFormDisabled: boolean = Boolean(isLoading || !!previewUserId);

  const newEntity: any = {
    companyId: entityId,
    userId: clientProfileUser?.id!,
    business_email: businessEmail,
    other_email: otherEmail,
    street: streetAddress,
    zip,
    city,
    state,
    country,
  };

  const onSaveEntity = async () => {
    try {
      setIsLoading(true);

      // This is a security measure since an advisor will have considerably more assigned companies than a client and so the entityId being passed could be for the wrong entity.
      if (isAdvisor) {
        throw new Error('Advisor editing a client profile');
      }

      await updateCompany({ ...newEntity, token });

      const newCompany: Company = {
        ...activeCompany,
        ...newEntity,
      };
      const updatedCompanies: Array<Company> = companies.map((company) =>
        company.id === newCompany.id ? newCompany : company,
      );

      setCompanies(updatedCompanies);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onBusinessEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setBusinessEmail(event.target.value);
  const onOtherEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setOtherEmail(event.target.value);
  const onStreetAddressChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setStreetAddress(event.target.value);
  const onCityChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCity(event.target.value);
  const onStateChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setState(event.target.value);
  const onZipChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setZip(event.target.value);
  const onCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setCountry(event.target.value);

  const activeCompany: Company | undefined = useMemo(
    () =>
      isPreview
        ? previewUserCompanies?.find((c) => c.id === entityId)
        : companies?.find((c) => c.id === entityId),
    [companies, isPreview, previewUserCompanies, entityId],
  );

  useEffect(() => {
    if (activeCompany?.id) {
      setBusinessEmail(activeCompany?.business_email || '');
      setOtherEmail(activeCompany?.other_email || '');
      setStreetAddress(activeCompany?.street || '');
      setState(activeCompany?.state || '');
      setCity(activeCompany?.city || '');
      setZip(activeCompany?.zip || '');
      setCountry(activeCompany?.country || '');
    }
    // eslint-disable-next-line
  }, [activeCompany?.id]);

  if (!activeCompany?.id) return null;

  return (
    <Flex gap={12} responsive="desktop">
      <SplitView>
        <Card
          header={activeCompany?.name}
          headerIcon={
            activeCompany?.entity_type === 'Individual' ? 'user' : 'buildings'
          }
        >
          <Flex gap={10} padding={10}>
            <Input
              label="Entity email"
              value={businessEmail}
              isDisabled={isFormDisabled}
              placeholder="john@example.com"
              onChange={onBusinessEmailChange}
            />
            <Input
              label="Other email"
              value={otherEmail}
              isDisabled={isFormDisabled}
              placeholder="john@example.com"
              onChange={onOtherEmailChange}
            />
          </Flex>
        </Card>
      </SplitView>
      <SplitView>
        <Card>
          <Flex isColumn={true} gap={8} padding={10}>
            <Flex isColumn={true} gap={8}>
              <Input
                label="Street Address"
                value={streetAddress}
                isDisabled={isFormDisabled}
                placeholder="10 Forest Ave"
                onChange={onStreetAddressChange}
              />
            </Flex>
            <Flex gap={8}>
              <Input
                label="City"
                value={city}
                isDisabled={isFormDisabled}
                placeholder="San Francisco"
                onChange={onCityChange}
              />
              <Input
                label="State"
                value={state}
                isDisabled={isFormDisabled}
                placeholder="CA"
                onChange={onStateChange}
              />
            </Flex>
            <Flex gap={8}>
              <Input
                label="Zip"
                value={zip}
                isDisabled={isFormDisabled}
                placeholder="94510"
                onChange={onZipChange}
              />
              <Select
                items={COUNTRY_ITEMS}
                label="Country"
                placeholder="Select"
                onChange={onCountryChange}
                isDisabled={isFormDisabled}
                selectedValue={country in countries ? country : ''}
              />
            </Flex>
            <ButtonGroup align="right">
              <Button
                intent="primary"
                isDisabled={isFormDisabled}
                isLoading={isLoading}
                value="Save Information"
                iconLeft="check"
                onClick={onSaveEntity}
              />
            </ButtonGroup>
          </Flex>
        </Card>
      </SplitView>
    </Flex>
  );
};
